import type { BasketItem, Product, ProductSize, SelectedOptionEntry } from '../types/order';
import { defaultSize, isFeeProduct, isSizeSellable, sortSizes } from './pricing';
import { autoFeeRequiredQty } from './autoFee';

/**
 * Επανάληψη παραγγελίας delivery (DeliveryWithMap reorderItems) — FE-5.
 *
 * ΓΙΑΤΙ: παλιότερα η οθόνη έφτιαχνε ΨΕΥΤΙΚΟ προϊόν με την ιστορική τιμή ως τιμή προϊόντος και
 * id από ταίριασμα ονόματος || 0: διπλή χρέωση στην επεξεργασία, 400 στην υποβολή για id 0, και
 * το όνομα αλλάζει και μοιάζει (CLAUDE.md: σύγκρινε με ταυτότητες).
 *
 * Εδώ κάθε γραμμή δένεται με το ΠΡΑΓΜΑΤΙΚΟ προϊόν/μέγεθος του τρέχοντος καταλόγου, πρώτα
 * με ταυτότητα (internal_product_id / size_id του GET λεπτομέρειας, BE-18) και μετά με
 * όνομα. Η τιμή μένει η ΙΣΤΟΡΙΚΗ (unit_price)· αν ο ταμίας επεξεργαστεί τη γραμμή, η οθόνη
 * την ξανατιμολογεί από τον κατάλογο (απόφαση P1-Q9_reorder_price, ίδια με F1-W3-8 του FE-4).
 *
 * Όσα δεν δένονται με βεβαιότητα ΠΑΡΑΛΕΙΠΟΝΤΑΙ και επιστρέφονται στο notFound για μήνυμα
 * στον ταμία — ποτέ γραμμή με id 0, ποτέ λάθος μέγεθος στην κουζίνα.
 *
 * ΤΕΛΗ — αποφάσεις ιδιοκτήτη F1-W6-2_reorder_platform_no_fee και F1-W6-3_reorder_gifted_fee_stays
 * (30/9, διορθώνουν το «0 περιπτώσεις» της F1-W5-11): η επανάληψη αναπαράγει την παλιά παραγγελία
 * και κρατά ΑΚΡΙΒΩΣ τα ιστορικά τέλη, σε ΚΑΘΕ περίπτωση. Γι' αυτό ΚΑΘΕ γραμμή της επανάληψης
 * παίρνει keepHistoricalFee:
 *  - τα είδη της επανάληψης ΔΕΝ γεννούν αυτόματο τέλος 16 (utils/autoFee)· στην πλατφόρμα
 *    (efood/Wolt/BOX) το τέλος είναι ήδη μέσα στην ιστορική τιμή — 923 παραγγελίες/30 ημ. στην
 *    παραγωγή (κατ. 4/6/7)· στο χαρισμένο/αφαιρεμένο τέλος η παραγγελία μένει χωρίς τέλος·
 *  - η ιστορική γραμμή τέλους μένει όπως ήρθε: ίδια ποσότητα, ίδιο ποσό/τεμ. (P1-Q9), εκτός
 *    αυτοματισμού — και όταν ισούται με ό,τι θα ζητούσε ο κανόνας (τότε απλώς δεν αλλάζει τίποτα
 *    ούτως ή άλλως· η ίδια διαδρομή για όλες τις περιπτώσεις = κανένα ποσό δεν αλλάζει ποτέ στη
 *    στιγμή της επανάληψης)·
 *  - ο κανόνας του αυτόματου τέλους ισχύει ΜΟΝΟ για ό,τι προσθέτει ή αλλάζει ο ταμίας μετά:
 *    νέο είδος (και από barcode: utils/barcodeLine δεν συγχωνεύει σε γραμμή επανάληψης) → δική
 *    του αυτόματη γραμμή στο τρέχον ποσό· επεξεργασία γραμμής → afterCashierEdit· ± / αφαίρεση
 *    είδους της επανάληψης → followRuleAfterCashierChange.
 *
 * ΜΕΤΑ ΑΠΟ ΑΛΛΑΓΗ ΤΟΥ ΤΑΜΙΑ (F1-W5-11 «η ποσότητα του τέλους 16 ακολουθεί τον κανόνα και μετά από
 * αλλαγές του ταμία», F1-W6-2/3 «ο κανόνας ισχύει μόνο μετά από αλλαγή του ταμία»): όταν τα ιστορικά
 * τέλη ΗΤΑΝ ο κανόνας (ιστορική γραμμή τέλους ίση με ό,τι ζητούν τα είδη), η πρώτη αλλαγή του ταμία
 * σε είδος της επανάληψης βάζει το καλάθι στον κανόνα: η ιστορική γραμμή γίνεται η «υπάρχουσα» του
 * αυτοματισμού — η ποσότητά της ακολουθεί τα είδη, το ποσό/τεμ. μένει το ιστορικό. Αλλιώς ένα καλάθι
 * με 2 FREDDO + 2 τέλη, όπου ο ταμίας έβγαζε τα FREDDO, χρέωνε 2 τέλη χωρίς κανένα είδος, και με
 * 5 FREDDO δήλωνε 2 τέλη στο παραστατικό. Πλατφόρμα/χαρισμένο/άνισο ΔΕΝ μπαίνουν στον κανόνα με
 * ±/αφαίρεση: στην πλατφόρμα το τέλος είναι μέσα στην ιστορική τιμή και ακολουθεί ήδη την ποσότητα
 * (αυτόματο τέλος εκεί = διπλή χρέωση)· χαρισμένο/άνισο = επιλογή του παλιού ταμία, εκκρεμεί
 * απόφαση ιδιοκτήτη για το τι γίνεται μετά από ±.
 * Η έκπτωση παραγγελίας ΔΕΝ πιάνει γραμμές τελών, ούτε τις ιστορικές (isStateFeeLine, F1-W5-11).
 */

export interface ReorderSourceLine {
  weight?: string | number | null;
  weight_unit?: string | null;
}

export function reorderWeightFields(item: ReorderSourceLine): { weight?: number; weightUnit?: string } {
  const w = item.weight == null || item.weight === '' ? NaN : Number(item.weight);
  // Μηδέν/αρνητικό/μη αριθμός = γραμμή χωρίς βάρος (τεμάχιο), όπως και στον server (weight > 0).
  if (!Number.isFinite(w) || w <= 0) return {};
  const unit = typeof item.weight_unit === 'string' && item.weight_unit.trim() !== '' ? item.weight_unit : undefined;
  return unit ? { weight: w, weightUnit: unit } : { weight: w };
}

/** Γραμμή του GET /api/orders/:storeId/order/:orderId (routes/orders.js, BE-18). */
export interface ReorderSourceItem extends ReorderSourceLine {
  product_name: string;
  quantity: number | string;
  /** NUMERIC → string. Για ζυγιζόμενο = τιμή ΟΛΗΣ της γραμμής (βάρος × τιμή κιλού). */
  unit_price: number | string;
  notes?: string | null;
  /** null σε γραμμές χωρίς ταυτότητα (παλιές/εσωτερικές)· μπορεί να έρθει και ως string. */
  internal_product_id?: number | string | null;
  /** internal_size_id· σήμερα γράφεται μόνο όταν κρύβεται το «Size:» (BE-16, απόφαση 15). */
  size_id?: number | string | null;
  /**
   * Η ίδια γραμμή όπως ήταν στο καλάθι του ταμείου (order_json.items, 15/10/2026): μέγεθος,
   * επιλογές, υλικά με ταυτότητες. null σε πλατφόρμες, παλιές παραγγελίες ή όταν οι γραμμές
   * δεν ταιριάζουν — τότε η επανάληψη δουλεύει από τη σημείωση, όπως πριν.
   */
  basket?: ReorderBasketLine | null;
}

export interface ReorderBasketLine {
  product_id?: number | null;
  size?: { id: number; name?: string | null } | null;
  selected_options?: SelectedOptionEntry[];
  added_extras?: BasketItem['addedExtras'];
  removed_ingredients?: unknown[];
  special_instructions?: string;
  notes?: string;
}

export interface ReorderResult {
  lines: BasketItem[];
  /** Όσα ΔΕΝ μπήκαν στο καλάθι: «προϊόν» ή «προϊόν (μέγεθος)». */
  notFound: string[];
}

const toId = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isInteger(n) && n > 0 ? n : null;
};

// Όνομα μεγέθους χωρίς τόνους/πεζά-κεφαλαία (παγίδα τόνων του CLAUDE.md· ίδιο με το FE-4).
const normName = (s: unknown) =>
  String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toLocaleUpperCase('el');

/** Το «Size: X» της σημείωσης γραμμής (utils/orderHelpers.buildDeliveryItemNotes). */
function noteSizeName(notes: string | null | undefined): string | null {
  if (!notes || !notes.includes('Size:')) return null;
  const m = notes.match(/Size:\s*([^|]+)/);
  const name = m ? m[1].trim() : '';
  return name || null;
}

/**
 * Μέγεθος κατά όνομα: πρώτα ακριβώς (όπως ως τώρα), αλλιώς χωρίς τόνους — ΜΟΝΟ αν ταιριάζει
 * ακριβώς ένα. Με δύο υποψήφια δεν ξέρουμε ποιο πουλήθηκε.
 */
function sizeByName(sizes: ProductSize[], name: string): ProductSize | null {
  const exact = sizes.find(s => s.name === name);
  if (exact) return exact;
  const n = normName(name);
  const loose = sizes.filter(s => normName(s.name) === n);
  return loose.length === 1 ? loose[0] : null;
}

/**
 * explicit = το μέγεθος βρέθηκε από ΡΗΤΗ πληροφορία της γραμμής (size_id ή «Size: X»). Το
 * χρησιμοποιεί το ταίριασμα κατά όνομα για να διαλέξει ανάμεσα σε ομώνυμα προϊόντα.
 */
type SizeResolution =
  | { ok: true; size: ProductSize | null; explicit: boolean }
  | { ok: false; label: string };

function resolveSize(product: Product, item: ReorderSourceItem): SizeResolution {
  // Τέλος: ΠΑΝΤΑ χωρίς μέγεθος (απόφαση 1_fees_source) — αλλιώς η επεξεργασία θα χρέωνε
  // ποσό τέλους + μέγεθος.
  if (isFeeProduct(product)) return { ok: true, size: null, explicit: false };

  // ΜΟΝΟ τα πωλήσιμα μεγέθη (F1-W5-11): γραμμή με μέγεθος που δεν πωλείται πια, σε προϊόν με
  // άλλα πωλήσιμα, παραλείπεται με μήνυμα στον ταμία. Ο κατάλογος του DeliveryWithMap είναι ήδη
  // φιλτραρισμένος (withSellableSizes)· το φίλτρο εδώ κάνει τον κανόνα ανεξάρτητο από τον καλούντα.
  const sizes = sortSizes((product.sizes || []).filter(isSizeSellable));
  // Προϊόν χωρίς πωλήσιμο μέγεθος έχει ΜΙΑ μορφή: ένα «Size: X» στη σημείωση δεν μπορεί να
  // στείλει άλλο είδος στην κουζίνα, άρα η γραμμή μπαίνει χωρίς μέγεθος (spec: «?? null»).
  // Χωρίς αυτό χανόταν από την επανάληψη ο «ΦΥΣΙΚΟΣ ΧΥΜΟΣ ΠΟΡΤΟΚΑΛΙ» 154 του κατ. 4 (σημείωση
  // «Size: Regular» από το ομώνυμο 393), που ως τώρα έμπαινε κανονικά.
  if (sizes.length === 0) return { ok: true, size: null, explicit: false };

  // Η στήλη της γραμμής πρώτα (όπως πριν), μετά το καλάθι του ταμείου (id, και όνομα για τα
  // μεγέθη που ξαναφτιάχτηκαν με νέο id).
  const sizeId = toId(item.size_id) ?? toId(item.basket?.size?.id);
  const basketSizeName = typeof item.basket?.size?.name === 'string' && item.basket.size.name.trim()
    ? item.basket.size.name.trim() : null;
  const noteName = noteSizeName(item.notes) ?? basketSizeName;

  if (sizeId !== null) {
    const byId = sizes.find(s => Number(s.id) === sizeId);
    if (byId) return { ok: true, size: byId, explicit: true };
  }
  if (noteName) {
    const byName = sizeByName(sizes, noteName);
    if (byName) return { ok: true, size: byName, explicit: true };
  }
  // Η γραμμή ΕΙΧΕ μέγεθος που δεν υπάρχει πια (κρυμμένο/αποσυρμένο/μετονομασμένο): χωρίς
  // μέγεθος θα έφευγε στην κουζίνα άλλο είδος και η επεξεργασία θα τιμολογούσε άλλο.
  if (sizeId !== null || noteName) {
    return { ok: false, label: noteName ?? `#${sizeId}` };
  }
  // Καμία πληροφορία μεγέθους: το προεπιλεγμένο, όπως τιμολογεί ήδη ο server (απόφαση 12) και
  // όπως το barcode — συνήθως προϊόν που ήταν χωρίς μέγεθος και μετά τη μετάπτωση έχει ένα
  // «Κανονικό». Η τιμή της γραμμής μένει η ιστορική· το μέγεθος μετρά για κουζίνα/συνταγή και
  // για την επεξεργασία (P1-Q9: τρέχουσα τιμή καταλόγου).
  return { ok: true, size: defaultSize(sizes), explicit: false };
}

type Match = { product: Product; size: ProductSize | null } | { skipped: string };

/**
 * Προϊόν + μέγεθος μιας γραμμής. Με id: αυτό το προϊόν (η ταυτότητα αποφασίζει). Χωρίς id (ή id
 * που δεν υπάρχει πια): ανάμεσα στα ΟΜΩΝΥΜΑ, πρώτα εκείνο όπου βρίσκεται ρητά το μέγεθος της
 * γραμμής, μετά το πρώτο που δέχεται τη γραμμή. Έτσι το αποτέλεσμα δεν εξαρτάται από τη σειρά
 * του καταλόγου (ORDER BY display_order, name — ισοπαλία στα ομώνυμα): στο κατ. 4 ο χυμός
 * «Size: Regular» δένεται με το 393 που έχει «Regular», όχι με το 154 χωρίς μεγέθη.
 */
function matchLine(item: ReorderSourceItem, cat: Product[]): Match {
  const pid = toId(item.internal_product_id) ?? toId(item.basket?.product_id);
  const byId = pid !== null ? cat.find(p => Number(p.id) === pid) : undefined;
  if (byId) {
    const r = resolveSize(byId, item);
    return r.ok ? { product: byId, size: r.size } : { skipped: `${byId.name} (${r.label})` };
  }

  const candidates = cat.filter(p => p.name === item.product_name);
  if (candidates.length === 0) return { skipped: String(item.product_name ?? '?') };

  let firstOk: { product: Product; size: ProductSize | null } | null = null;
  let firstSkip: string | null = null;
  for (const product of candidates) {
    const r = resolveSize(product, item);
    if (!r.ok) {
      if (firstSkip === null) firstSkip = `${product.name} (${r.label})`;
      continue;
    }
    if (r.explicit) return { product, size: r.size };
    if (!firstOk) firstOk = { product, size: r.size };
  }
  return firstOk ?? { skipped: firstSkip as string };
}

interface ParsedNotes {
  removedIngredients: any[];
  addedExtras: any[];
  specialInstructions: string;
}

/** Ανάλυση της σημείωσης όπως ως τώρα στο DeliveryWithMap (χωρίς αλλαγή συμπεριφοράς). */
function parseNotes(notes: string | null | undefined): ParsedNotes {
  const out: ParsedNotes = { removedIngredients: [], addedExtras: [], specialInstructions: '' };
  if (!notes) return out;

  if (notes.includes('Without:')) {
    const withoutMatch = notes.match(/Without:\s*([^|]+)/);
    if (withoutMatch) {
      out.removedIngredients = withoutMatch[1].split(',')
        .map((name: string) => name.trim())
        .map((name: string, idx: number) => ({ id: idx, name }));
    }
  }

  if (notes.includes('Extra:')) {
    const extraMatch = notes.match(/Extra:\s*([^|]+)/);
    if (extraMatch) {
      out.addedExtras = extraMatch[1].split(',').map((e: string) => e.trim()).map((extraStr: string, idx: number) => {
        const match = extraStr.match(/(.+)\s*x(\d+)/);
        if (match) return { id: idx, name: match[1].trim(), quantity: parseInt(match[2], 10) };
        return { id: idx, name: extraStr, quantity: 1 };
      });
    }
  }

  // Ό,τι μένει μετά το τελευταίο | και δεν είναι τυποποιημένο πεδίο = οδηγίες.
  const parts = notes.split('|');
  const lastPart = parts[parts.length - 1].trim();
  if (lastPart && !lastPart.startsWith('Size:') && !lastPart.startsWith('Without:') && !lastPart.startsWith('Extra:')) {
    out.specialInstructions = lastPart;
  }
  return out;
}

/**
 * Τα πεδία της γραμμής που έρχονται από το καλάθι του ταμείου (item.basket), όταν υπάρχει.
 * Επιλογές: όπως πουλήθηκαν (η τιμή της γραμμής είναι ούτως ή άλλως η ιστορική· αν ο ταμίας
 * ανοίξει τη γραμμή, το παράθυρο φορτώνει τις τρέχουσες ομάδες). «Χωρίς»: τα ids του καλαθιού με
 * τα ονόματα της σημείωσης, όταν ταιριάζουν σε πλήθος — αλλιώς η κουζίνα θα τύπωνε «ID:12».
 * Η σημείωση γίνεται του καλαθιού (π.χ. «Size: Διπλός»), όχι η συνθεμένη της γραμμής, που ο
 * server θα ξαναπρόσθετε κάθε φορά.
 */
function basketDetails(basket: ReorderBasketLine | null | undefined, parsed: ParsedNotes): Partial<BasketItem> {
  if (!basket || typeof basket !== 'object') return {};
  const out: Partial<BasketItem> = {
    specialInstructions: typeof basket.special_instructions === 'string' ? basket.special_instructions : '',
    notes: typeof basket.notes === 'string' ? basket.notes : undefined,
  };
  if (Array.isArray(basket.selected_options) && basket.selected_options.length > 0) {
    out.selected_options = basket.selected_options;
  }
  if (Array.isArray(basket.added_extras) && basket.added_extras.length > 0) {
    out.addedExtras = basket.added_extras;
  }
  const removed = Array.isArray(basket.removed_ingredients) ? basket.removed_ingredients : [];
  if (removed.length > 0) {
    const named = removed.every(r => r && typeof r === 'object' && typeof (r as { name?: unknown }).name === 'string');
    const ids = removed.map(r => Number(r));
    if (named) {
      out.removedIngredients = removed as any[];
    } else if (ids.every(n => Number.isInteger(n) && n > 0) && parsed.removedIngredients.length === ids.length) {
      out.removedIngredients = ids;
      out.removedIngredientNames = parsed.removedIngredients.map((r: { name: string }) => r.name);
    }
    // αλλιώς μένουν τα «Χωρίς» της σημείωσης (με ονόματα), όπως πριν
  }
  return out;
}

/**
 * Γραμμές καλαθιού από τις γραμμές μιας προηγούμενης παραγγελίας, πάνω στον ΤΡΕΧΟΝΤΑ
 * κατάλογο POS (products της οθόνης: διαθέσιμα, ορατά, μόνο πωλήσιμα μεγέθη). Δεν αλλάζει
 * τις εισόδους. `now` μόνο για σταθερά ids στα tests.
 */
export function buildReorderLines(
  items: ReorderSourceItem[] | null | undefined,
  catalog: Product[] | null | undefined,
  now: number = Date.now()
): ReorderResult {
  const lines: BasketItem[] = [];
  const notFound: string[] = [];
  if (!Array.isArray(items)) return { lines, notFound };
  const cat = Array.isArray(catalog) ? catalog : [];

  items.forEach((item, index) => {
    const match = matchLine(item, cat);
    if ('skipped' in match) {
      notFound.push(match.skipped);
      return;
    }
    const { product } = match;

    // Η ιστορική τιμή (P1-Q9): ό,τι χρεώθηκε τότε, ως αριθμός (το NUMERIC έρχεται string).
    const price = Number(item.unit_price) || 0;
    const parsed = parseNotes(item.notes);
    const fromBasket = basketDetails(item.basket, parsed);
    lines.push({
      id: `reorder-${index}-${now}`,
      product,
      selectedSize: match.size,
      quantity: Number(item.quantity) || 0,
      price,
      totalPrice: price,
      // Ζυγιζόμενο: το unit_price είναι η τιμή όλης της γραμμής· χωρίς βάρος ο server θα
      // την έκρινε ως 1 τεμάχιο στην τιμή κιλού (ψευδές 403, BE-08).
      ...reorderWeightFields(item),
      removedIngredients: parsed.removedIngredients,
      addedExtras: parsed.addedExtras,
      specialInstructions: parsed.specialInstructions,
      notes: item.notes ?? undefined,
      // Με το καλάθι του ταμείου: επιλογές/υλικά/οδηγίες ΑΚΡΙΒΩΣ όπως πουλήθηκαν (15/10/2026).
      ...fromBasket,
      // Σημάδι προέλευσης: η τιμή της γραμμής είναι η ιστορική (P1-Q9).
      fromReorder: true,
      // Ο ΚΑΝΟΝΑΣ της επανάληψης για τα τέλη (F1-W6-2/F1-W6-3, βλ. κεφαλίδα): ΚΑΘΕ γραμμή, χωρίς
      // σύγκριση με τον κανόνα. Οι ιστορικές γραμμές τέλους μένουν και χωριστές όπως ήρθαν (όχι
      // ένωση ίδιων ποσών): αναπαράγουν την παλιά παραγγελία, και δεν τις διαχειρίζεται ο αυτοματισμός
      // ώσπου να αλλάξει ο ταμίας ένα είδος (afterCashierEdit / followRuleAfterCashierChange).
      keepHistoricalFee: true,
    });
  });

  return { lines, notFound };
}

/** Ποσό ευρώ → ακέραια λεπτά (σύγκριση τιμών χωρίς σφάλμα κινητής υποδιαστολής)· σκουπίδι → NaN. */
const toCents = (v: unknown): number => {
  const n = Number(v);
  return v === null || v === undefined || v === '' || !Number.isFinite(n) ? NaN : Math.round(n * 100);
};

const unitOf = (b: BasketItem) => b.price ?? b.totalPrice;
const sizeIdOf = (b: BasketItem) => (b.selectedSize ? Number(b.selectedSize.id) : null);

function withoutKeep(b: BasketItem): BasketItem {
  if (!b.keepHistoricalFee) return b;
  const { keepHistoricalFee, ...rest } = b;
  return rest;
}

/**
 * Γραμμή επανάληψης μετά από ΕΠΕΞΕΡΓΑΣΙΑ του ταμία (DeliveryWithMap updateBasketItem).
 * `original` = η γραμμή πριν το modal, `edited` = η ίδια μετά (ξανατιμολογημένη από τον κατάλογο).
 *
 * ΓΙΑΤΙ: η επεξεργασία ξανατιμολογεί τη γραμμή από τον κατάλογο (P1-Q9), και οι τιμές καταλόγου
 * είναι ΧΩΡΙΣ το τέλος. Όταν η ιστορική τιμή χάνεται (άλλο ποσό μονάδας) ή αλλάζει το είδος (άλλο
 * μέγεθος), χάνεται και το ιστορικό τέλος της γραμμής, άρα ο κανόνας του αυτόματου τέλους πιάνει
 * πλέον αυτή τη γραμμή («ο κανόνας τέλους ενεργοποιείται ΜΟΝΟ αν ο ταμίας αλλάξει τα είδη», F1-W6-2·
 * «ο κανόνας ισχύει μόνο μετά από αλλαγή του ταμία», F1-W6-3). Πλατφόρμα: 2,75 (με τέλος) → 2,70 +
 * αυτόματο 0,05, ίδιο σύνολο.
 *
 * ΙΔΙΟ ποσό μονάδας ΚΑΙ ίδιο μέγεθος (π.χ. μόνο σημείωση, «χωρίς ζάχαρη», ή άνοιγμα-αποθήκευση): η
 * γραμμή ΜΕΝΕΙ ιστορική. Σε χαρισμένο τέλος (F1-W6-3) μια σημείωση δεν αναιρεί σιωπηλά τη χάρη για
 * όλη την ποσότητα· καμία αλλαγή ποσού χωρίς αλλαγή ποσού του είδους. Τα υλικά/έξτρα/επιλογές δεν
 * συγκρίνονται πεδίο-πεδίο: στη γραμμή επανάληψης έρχονται από τη σημείωση ({id: θέση, name}) και
 * από το modal ως ids, άρα θα «διέφεραν» πάντα· ό,τι από αυτά αλλάζει ποσό φαίνεται στην τιμή.
 * (Το τι μετρά ως «αλλαγή του ταμία» σε χαρισμένο θέλει επιβεβαίωση ιδιοκτήτη — βλ. αναφορά.)
 *
 * ΕΞΑΙΡΕΣΗ — υπάρχει ακόμη στο καλάθι ιστορική γραμμή τέλους 16 (με keepHistoricalFee): το τέλος
 * της γραμμής είναι ήδη ΕΚΕΙ· αν η γραμμή έμπαινε μόνη της στον κανόνα θα χρεωνόταν δεύτερη φορά
 * (ιστορική + αυτόματη). Μένει ιστορική· αν το καλάθι ήταν στον κανόνα, το followRuleAfterCashierChange
 * που καλεί μετά η οθόνη βάζει ΟΛΟ το καλάθι στον κανόνα με την ιστορική γραμμή ως «υπάρχουσα».
 *
 * Γραμμές που δεν είναι της επανάληψης, και γραμμές τελών, επιστρέφονται ίδιες.
 */
export function afterCashierEdit(original: BasketItem, edited: BasketItem, basket: BasketItem[]): BasketItem {
  if (!edited.keepHistoricalFee || edited.product.fee_category != null) return edited;
  const samePrice = toCents(unitOf(original)) === toCents(unitOf(edited));
  if (samePrice && sizeIdOf(original) === sizeIdOf(edited)) return edited;
  const historicalFeeLine = basket.some(b => b.keepHistoricalFee && b.product.fee_category === 16);
  if (historicalFeeLine) return edited;
  return withoutKeep(edited);
}

/**
 * Μετά από αλλαγή του ταμία σε ΕΙΔΟΣ της επανάληψης (± / αφαίρεση / επεξεργασία στο DeliveryWithMap):
 * αν τα ιστορικά τέλη ΗΤΑΝ ο κανόνας, το καλάθι μπαίνει στον κανόνα (F1-W5-11 + F1-W6-2/3, βλ.
 * κεφαλίδα). `prev` = καλάθι πριν την αλλαγή, `next` = μετά, `changedId` = η γραμμή που άλλαξε.
 *
 * Μπαίνει στον κανόνα ΜΟΝΟ όταν ισχύουν ΟΛΑ στο `prev` (όπως το followsRule του phase1):
 *  - η γραμμή που άλλαξε είναι είδος (όχι τέλος) με keepHistoricalFee·
 *  - ο ταμίας δεν έχει δική του επιλογή τέλους (feeOverride null)· με επιλογή, αυτή υπερισχύει και
 *    ο κανόνας θα την εφάρμοζε σε όλο το τέλος·
 *  - υπάρχει ιστορική γραμμή τέλους 16 (ΟΧΙ πλατφόρμα/χαρισμένο), όλες του ΤΡΕΧΟΝΤΟΣ προϊόντος-τέλους·
 *  - ΟΛΕΣ οι γραμμές αυτού του προϊόντος-τέλους (ιστορικές + η αυτόματη νέων ειδών) έχουν ΙΔΙΟ ποσό/τεμ.:
 *    γίνονται μία γραμμή, και με άλλο ποσό η ένωση θα άλλαζε ποσό·
 *  - το σύνολό τους = ό,τι ζητά ο κανόνας για ΟΛΑ τα είδη του καλαθιού (ίσο, όχι άνισο/χειροκίνητο).
 * Τότε: φεύγει το keepHistoricalFee από όλες τις γραμμές και οι γραμμές του προϊόντος-τέλους γίνονται
 * ΜΙΑ (στη θέση της πρώτης ιστορικής, ίδιο ποσό × ίδιο άθροισμα = κανένα ποσό δεν αλλάζει εδώ). Το
 * effect του αυτόματου τέλους τη βρίσκει ως «υπάρχουσα» και αλλάζει ΜΟΝΟ την ποσότητα κατά τον κανόνα.
 * Αλλιώς επιστρέφεται το `next` αυτούσιο (τα ιστορικά τέλη μένουν).
 */
export function followRuleAfterCashierChange(
  prev: BasketItem[],
  next: BasketItem[],
  changedId: string,
  feeProduct: Product | null | undefined,
  feeOverride: number | null
): BasketItem[] {
  const changed = prev.find(b => b.id === changedId);
  if (!changed || !changed.keepHistoricalFee || changed.product.fee_category != null) return next;
  if (!feeProduct || feeProduct.fee_category !== 16 || feeOverride !== null) return next;

  const feeId = Number(feeProduct.id);
  const hist = prev.filter(b => b.keepHistoricalFee && b.product.fee_category === 16);
  if (hist.length === 0 || !hist.every(h => Number(h.product.id) === feeId)) return next;

  const feeLines = prev.filter(b => b.product.fee_category === 16 && Number(b.product.id) === feeId);
  const unit = toCents(unitOf(hist[0]));
  if (!Number.isFinite(unit) || !feeLines.every(f => toCents(unitOf(f)) === unit)) return next;

  const feeQty = feeLines.reduce((s, f) => s + (Number(f.quantity) || 0), 0);
  const required = autoFeeRequiredQty(prev.filter(b => b.product.fee_category == null).map(withoutKeep));
  if (feeQty !== required) return next;

  // Η αλλαγή αφορά είδος, άρα οι γραμμές τέλους του `next` είναι ίδιες με του `prev`.
  const mergeIds = new Set(feeLines.map(f => f.id));
  const keeperId = hist[0].id;
  const out: BasketItem[] = [];
  for (const b of next) {
    if (mergeIds.has(b.id) && b.id !== keeperId) continue;
    out.push(withoutKeep(b.id === keeperId ? { ...b, quantity: feeQty } : b));
  }
  return out;
}

/**
 * Γραμμή τέλους 16 που ΜΠΟΡΕΙ να τη διαχειριστεί ο αυτοματισμός. Ποια ακριβώς τη διαχειρίζεται (και
 * περνά στο καλάθι ως «αυτόματη») το λέει το autoManagedFeeLineId του utils/autoFee: η δική του
 * γραμμή πρώτα — μια χειροκίνητη δίπλα της είναι του ταμία (19/10/2026).
 * Η ιστορική γραμμή τέλους της επανάληψης (keepHistoricalFee) ΔΕΝ είναι:
 * δεν την αγγίζει το effect, άρα ± αλλάζει τη δική της ποσότητα — αλλιώς το ± θα έγραφε
 * feeOverride στην αυτόματη γραμμή των νέων ειδών και θα έβγαζε/πρόσθετε τέλος αλλού.
 */
export function isAutoFeeLine(item: BasketItem): boolean {
  return item.product.fee_category === 16 && !item.keepHistoricalFee;
}

/**
 * Γραμμή τέλους του καλαθιού delivery ως ΤΕΛΟΣ: εκτός έκπτωσης παραγγελίας.
 *
 * ΚΑΙ η ιστορική γραμμή τέλους της επανάληψης (fromReorder): απόφαση F1-W5-11 — «η έκπτωση
 * παραγγελίας δεν εφαρμόζεται σε γραμμές τελών». Ως το FE-5 εξαιρούνταν (ψεύτικο προϊόν χωρίς
 * fee_category → με έκπτωση).
 */
export function isStateFeeLine(item: BasketItem): boolean {
  return item.product.fee_category != null;
}
